import React from "react";
import { Product } from "@/types/product";
import { useStorefrontEngine } from "@/hooks/useStorefrontEngine";
import { StoreVideoPlayer } from "./StoreVideoPlayer";
import { StorefrontProductGrid } from "./StorefrontProductGrid";
import { PremiumNavbar } from "./PremiumNavbar";
import { PremiumFooter } from "./PremiumFooter";
import { TrustSection } from "./TrustSection";
import { ShoppingBag } from "lucide-react";
import { StoreSEO } from "@/components/seo/StoreSEO";
import { T } from "@/components/translation/T";
import { useStoreLanguages } from "@/hooks/useStoreLanguages";
import { useStoreContentTranslations, type ContentSource } from "@/hooks/useStoreContentTranslations";
import { useMemo } from "react";
import { useStoreCurrency } from "@/lib/storeCurrency";

interface LiveStorefrontProps {
  products: Product[];
  storeName: string | null;
  isLoading: boolean;
  storeId?: string | null;
}

export function LiveStorefront({ products, storeName, isLoading, storeId }: LiveStorefrontProps) {
  const { isLoading: isEngineLoading } = useStorefrontEngine(storeId);
  const loading = isLoading || isEngineLoading;
  const langs = useStoreLanguages(storeId);
  const contentSources = useMemo<ContentSource[]>(
    () => products.map((p) => ({ table: "products" as const, id: p.id, fields: { name: p.name } })),
    [products],
  );
  const { tr } = useStoreContentTranslations(storeId, langs.currentLanguage, langs.defaultLanguage, langs.enabledLanguages, contentSources);

  const currency = useStoreCurrency(storeId);
  const featuredProducts = products.slice(0, 6);
  const allProducts = products;

  const seoTitle = storeName || "Boutique";
  const seoDescription = storeName
    ? `Découvrez la collection de ${storeName} — produits sélectionnés, livraison rapide.`
    : "Découvrez notre collection de produits soigneusement sélectionnés.";
  const ogImage = featuredProducts[0]?.image || undefined;

  return (
    <div className="min-h-screen bg-white">
      <StoreSEO
        title={seoTitle}
        description={seoDescription}
        storeName={storeName || undefined}
        ogImage={ogImage}
      />
      {/* Premium Navbar */}
      <PremiumNavbar storeName={storeName} />

      {/* Hero Video */}
      {storeId && <StoreVideoPlayer storeId={storeId} storeName={storeName} />}

      {/* Main Content */}
      <main>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900" />
          </div>
        ) : (
          <>
            {/* Featured Products */}
            {featuredProducts.length > 0 && (
              <section id="featured-products" className="py-20 md:py-28 px-6">
                <div className="max-w-[1200px] mx-auto">
                  <div className="text-center mb-14 animate-fade-in">
                    <p className="text-xs font-semibold uppercase tracking-[3px] text-neutral-400 mb-3">
                      <T>Sélection</T>
                    </p>
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">
                      <T>Produits en vedette</T>
                    </h2>
                  </div>
                  <StorefrontProductGrid products={featuredProducts} tr={tr} currency={currency} />
                </div>
              </section>
            )}

            {/* All Products */}
            {allProducts.length > 6 && (
              <section id="all-products" className="py-20 md:py-28 px-6">
                <div className="max-w-[1200px] mx-auto">
                  <div className="text-center mb-14 animate-fade-in">
                    <p className="text-xs font-semibold uppercase tracking-[3px] text-neutral-400 mb-3">
                      <T>Collection complète</T>
                    </p>
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">
                      <T>Tous les produits</T>
                    </h2>
                  </div>
                  <StorefrontProductGrid products={allProducts} tr={tr} currency={currency} />
                </div>
              </section>
            )}

            {/* Trust */}
            <TrustSection />

            {/* Empty State */}
            {products.length === 0 && (
              <div className="text-center py-24 space-y-4 animate-fade-in">
                <ShoppingBag className="h-16 w-16 mx-auto text-neutral-300" />
                <h2 className="text-xl font-semibold text-neutral-500">
                  <T>Aucun produit disponible pour le moment</T>
                </h2>
                <p className="text-sm text-neutral-400">
                  <T>Revenez bientôt pour découvrir notre collection.</T>
                </p>
              </div>
            )}
          </>
        )}
      </main>

      {/* Premium Footer */}
      <PremiumFooter storeName={storeName} languages={langs} />
    </div>
  );
}
